import {
  AreaFormat,
  escapeHtml,
  FloorPlanElement,
  initializePlan,
  isBlockOfFlats,
  isFloor,
  isProperty,
  MoneyFormat,
  PlanElement,
  PlanElementType,
  PlanOptions,
  PlanProperty,
  PropertyType,
  propertyTypeToString
} from "@vpsoftware/vprealestate-www";

export const planOptions: PlanOptions = {
  styles: {
    Regular: { fill: 'rgb(247, 215, 128, 0.2)', stroke: 'rgb(247, 215, 128, 0.8)', fillHover: 'rgb(247, 215, 128, 0.4)', strokeHover: 'rgb(247, 215, 128, 1)'},
    Available: { fill: 'rgb(0, 215, 0, 0.2)', stroke: 'rgb(0, 215, 0, 0.8)', fillHover: 'rgb(0, 215, 0, 0.4)', strokeHover: 'rgb(0, 215, 0, 1)'},
    Reserved: { fill: 'rgb(255, 255, 0, 0.2)', stroke: 'rgb(255, 255, 0, 0.4)', fillHover: 'rgb(255, 255, 0, 0.4)', strokeHover: 'rgb(255, 255, 0, 0.4)'},
    Sold: { fill: 'rgb(255, 0, 0, 0.2)', stroke: 'rgb(255, 0, 0, 0.4)', fillHover: 'rgb(255, 0, 0, 0.4)', strokeHover: 'rgb(255, 0, 0, 0.4)'}
  },
  getPopoverTitle: popoverTitle,
  getPopoverContent: popoverContent,
  onClick: elementClick,
  getElementStyle
};

export const propertyPlanOptions: PlanOptions = {
  styles: {
    Regular: { fill: 'rgb(247, 215, 128, 0.4)', stroke: 'rgb(247, 215, 128, 0.4)'}
  },
  popoversEnabled: false,
  getElementStyle: _ => 'Regular'
};

function getElementStyle(element: PlanElement) {
  if (isProperty(element)) {
    return element.st;
  }
  return 'Regular';
}

function elementClick(element: PlanElement) {
  if (isFloor(element)) {
    loadFloor(element);
    return;
  }
  if (!element.url) return;
  if( element.t == PlanElementType.Property)
    document.location = element.url + '#propertySection';
  else 
    document.location = element.url;
}

function loadFloor(floor: FloorPlanElement) {
  if (!floor.plUrl || !floor.pr) return;

  let floorPlanContainer = document.getElementById('floor-plan-container');
  let hasPrev = false;
  floorPlanContainer.childNodes.forEach(x => {
    if (!(x instanceof HTMLElement)) return;
    if (x.dataset.floor === '' + floor.fl) {
      x.classList.remove('d-none');
      hasPrev = true;
    }
    x.classList.add('d-none');
  });
  if (hasPrev) return;
  let newPicture = document.createElement('picture');
  newPicture.innerHTML = `
        <source srcset="${floor.plUrl}?format=webp" type="image/webp"/>
        <img src="${floor.plUrl}" alt="${floor.n} - plan"/>`;

  newPicture.classList.add('floor-plan', 'clickable-plan', 'plan-image');
  newPicture.dataset.floor = '' + floor.fl;

  floorPlanContainer.appendChild(newPicture);
  initializePlan(newPicture, floor.pr, planOptions);
  floorPlanContainer.scrollIntoView({behavior: 'smooth', block: 'center'});
  
  let estateEntries = document.querySelectorAll<HTMLDivElement>('.estate-list-section .estate-list-row');
  estateEntries.forEach(e => {
    e.classList.toggle('d-none', e.dataset.floor !== '' + floor.fl); 
  });
  
  let estateSections = document.querySelectorAll<HTMLElement>('.estate-list-section');
  estateSections.forEach(section => {
    let visibleRows = section.querySelectorAll('.estate-list-row:not(.d-none)');
    let moreSectionLayer = section.nextElementSibling as HTMLElement;

    if (visibleRows.length === 0) {
      section.classList.add('d-none');
      if (moreSectionLayer && moreSectionLayer.classList.contains('more-section-layer')) {
        moreSectionLayer.classList.add('d-none');
      }
    } else {
      let container = section.querySelector('div.container');
      if (container) {
        container.classList.remove('collapsed');
      }
      if (moreSectionLayer && moreSectionLayer.classList.contains('more-section-layer')) {
        moreSectionLayer.classList.add('d-none');
      }
      section.classList.remove('d-none');
    }
  });
}

function popoverTitle(_: PlanElement) {
  return null;
}

function popoverContent(obj: PlanElement) {
  if (isBlockOfFlats(obj) || isFloor(obj)) {
    return formatPropertyStats(obj);
  }
  if (isProperty(obj)) {
    let result = '<div class="popover-info">';
    let typeStr = propertyTypeToString(obj.pt);
    let numberStr = `${typeStr} nr ${obj.pn}`;
    result += `<div class="span-all primary-text fw-bold fs-3 text-nowrap">${escapeHtml(numberStr)}</div>`;

    if(obj.isChild && !obj.dynamicOwner && obj.ownerAddress){
      result += `<div class="info text-nowrap">Przypisane do <strong>${obj.ownerAddress}</strong></div>`
      result += '</div>';
      return result;
    }

    if (obj.a) {
      result += `<div class="descr">Powierzchnia:</div><div>${AreaFormat.format(obj.a)}&nbsp;m²</div>`;
    }

    if (obj.tr) {
      if (obj.pt === PropertyType.Premises) {
        result += `<div class="descr">Ilość pomieszczeń:</div><div>${obj.tr}</div>`;
      } else {
        result += `<div class="descr">Ilość pokoi:</div><div>${obj.tr}</div>`;
      }
    }

      if (obj.tp && obj.st !== 'Sold') {
          result += `<div class="descr">Cena brutto:</div>`;
          if (obj.top && obj.top > obj.tp)
              result += `<div><div class="old-price">${MoneyFormat.format(obj.top)}&nbsp;zł</div>`;
          result += `<div>${MoneyFormat.format(obj.tp)}&nbsp;zł</div>`;
          result += (obj.top && obj.top > obj.tp) ? `</div>` : '';
      }

    if (obj.npr && obj.st !== 'Sold') {
      result += `<div class="descr">Cena netto:</div><div>`;

      if (obj.npr && obj.norgPr > obj.npr) {
        result += `<div class="old-price">${MoneyFormat.format(obj.norgPr)}&nbsp;zł</div>`;
      }
      result += `<div>${MoneyFormat.format(obj.npr)}&nbsp;zł</div></div>`;
    }

      if(obj.childProperties && obj.childProperties.length > 0){
          let objPrice = '';
          if(obj.orgPr && obj.orgPr > obj.pr){
              objPrice += `<div class="d-flex flex-column text-secondary">`;
              objPrice += `<div class="old-price">${MoneyFormat.format(obj.orgPr)}&nbsp;zł</div>`;
              objPrice += `<div>${MoneyFormat.format(obj.pr)}&nbsp;zł</div>`;
              objPrice += `</div>`
          }
          else {
              objPrice = `${MoneyFormat.format(obj.pr)}&nbsp;zł`;
          }
          if(obj.st != 'Sold'){
              if(obj.pt == PropertyType.Flat){
                  result += `<div class="descr text-nowrap bullet-point text-secondary">Mieszkanie: </div><div>${objPrice}</div>`;
              }else if (obj.pt == PropertyType.House) {
                  result += `<div class="descr text-nowrap bullet-point text-secondary">Dom: </div><div>${objPrice}</div>`;
              }else if (obj.pt == PropertyType.Premises) {
                  result += `<div class="descr text-nowrap bullet-point text-secondary">Lokal: </div><div>${objPrice}</div>`;
              }
          }

          obj.childProperties.forEach(childProperty => {
              let price = '';
              if(childProperty.orgPr && childProperty.orgPr > childProperty.pr){
                  price += `<div class="d-flex flex-column text-secondary">`;
                  price += `<div class="old-price">${MoneyFormat.format(childProperty.orgPr)}&nbsp;zł</div>`;
                  price += `<div>${MoneyFormat.format(childProperty.pr)}&nbsp;zł</div>`;
                  price += `</div>`
              }
              else if(childProperty.pr) {
                  price = `${MoneyFormat.format(childProperty.pr)}&nbsp;zł`;
              }
              result += `<div class="descr text-nowrap text-secondary bullet-point">${childProperty.name}`;
              if(obj.st !== 'Sold') {
                  result += `: </div><div class="text-secondary">${price}</div>`
              }else{
                  result += `</div>`
              }
              if(childProperty.features && childProperty.features.length > 0){
                  childProperty.features.filter(x => x.price).forEach(feature => {
                      result += `<div class="descr text-nowrap text-secondary bullet-point-further">${feature.name}`;
                      if(obj.st !== 'Sold') {
                          result += `: </div><div class="text-secondary">${MoneyFormat.format(feature.price)}&nbsp;zł</div>`;
                      }else{
                          result += `</div>`
                      }
                  });
              }
          });
      }

      if(obj.features && obj.features.length > 0){
          obj.features.filter(x => x.price).forEach(feature => {
              result += `<div class="descr text-nowrap text-secondary bullet-point">${feature.name}`;
              if(obj.st !== 'Sold') {
                  result += `: </div><div class="text-secondary">${MoneyFormat.format(feature.price)}&nbsp;zł</div>`;
              }else{
                  result += `</div>`
              }
          });
      }
    
    switch (obj.st) {
      case "Available":
        result += 
          `<div class="span-all text-center">
            <span class="estate-label estate-label-available"><i class="bi bi-circle-fill"></i> Dostępne</span>
          </div>`;
        break;
      case "Reserved":
        result += 
          `<div class="span-all text-center">
            <span class="estate-label estate-label-reservation"><i class="bi bi-circle-fill"></i> Rezerwacja - Zadzwoń</span>
          </div>`;
        break;
      case "Sold":
        result += 
          `<div class="span-all text-center">
            <span class="estate-label estate-label-sold"><i class="bi bi-circle-fill"></i> Sprzedane</span>
          </div>`;
        break;
    }
    
    result += '</div>';
    return result;
  }
}

function formatPropertyStats(obj: {pr?: PlanProperty[], n: string}) {
  let result = '<div class="popover-info">';
  
  result += `<div class="span-all primary-text fw-bold fs-3">${escapeHtml(obj.n)}</div>`;
  
  if (obj.pr) {
    let reserved = 0;
    let sold = 0;
    let available = 0;
    obj.pr.forEach(prop => {
      switch (prop.st) {
        case 'Available':
          ++available;
          break;
        case "Reserved":
          ++reserved;
          break;
        case "Sold":
          ++sold;
          break;
      }
    });
    
    if (available > 0) {
      result += `<div>Dostępne</div><div>${available} ${declination(available)}</div>`;
    }
    if (reserved > 0) {
      result += `<div>Zarezerwowane</div><div>${reserved} ${declination(reserved)}</div>`;
    }
    if (sold > 0) {
      result += `<div>Sprzedane</div><div>${sold} ${declination(sold)}</div>`;
    }
  }

  result += `</div>`;
  return result;
}

function declination(value: number) {
  if (value < 1) return 'mieszkań';
  if (value === 1) return 'mieszkanie';
  if (value < 5) return 'mieszkania';
  return 'mieszkań';
}
